/**
 * PGLP | ABA ABERTURA — Resumo + Editar + Imóveis + Excluir
 * ARQUIVO: /src/components/laudo/abas/AbaAbertura.jsx
 * VERSÃO: 2.2.0
 * ------------------------------------------------------------
 * NOVIDADES v2.2.0:
 *   ✔ Botão de exclusão em cada imóvel (ícone lixeira)
 *   ✔ Modal de confirmação com digitação do UUID
 *   ✔ Feedback visual durante exclusão
 *   ✔ Chama /api/imovel/excluir.php
 * ============================================================
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Loader2, FileText, Clock, User, Gavel, Target,
    Edit3, X, Save, Plus, MapPin, ChevronRight,
    AlertTriangle, Trash2, ShieldAlert,
} from 'lucide-react';

const OBJETIVOS   = ['VALOR DE MERCADO', 'GARANTIA REAL', 'INDENIZATÓRIO', 'LIQUIDAÇÃO FORÇADA'];
const FINALIDADES = ['JUDICIAL', 'BANCARIA', 'ADMINISTRATIVA'];
const ESFERAS     = ['JUDICIAL', 'EXTRA'];

/* ---------- Helpers ---------- */

function extrairUuidImovel(im) {
    return im?.imovel_uuid || im?.uuid || im?.imovel?.id || im?.dados?.id || null;
}

function extrairDossie(im) {
    return im?.imovel || im?.dados || null;
}

function enderecoDoImovel(im, idx) {
    const d = extrairDossie(im) || {};
    const id = d.identificacao || {};
    const loc = d.localizacao || {};

    const logradouro =
        loc.logradouro?.valor ||
        id.endereco?.valor ||
        d.logradouro ||
        '';
    const numero = loc.numero?.valor || d.numero || '';
    const bairro = loc.bairro?.valor || d.bairro || '';

    const partes = [logradouro, numero].filter(Boolean).join(', ');
    if (partes) return partes;
    if (bairro) return bairro;
    return `Imóvel ${idx + 1}`;
}

/* ============================================================
   COMPONENTE
============================================================ */

export default function AbaAbertura() {
    const { codigo } = useParams();
    const navigate = useNavigate();

    const [laudo, setLaudo]             = useState(null);
    const [imoveis, setImoveis]         = useState([]);
    const [loading, setLoading]         = useState(true);
    const [erro, setErro]               = useState(null);
    const [modalAberto, setModalAberto] = useState(false);
    const [salvando, setSalvando]       = useState(false);
    const [erroModal, setErroModal]     = useState(null);
    const [form, setForm] = useState({ objetivo: '', finalidade: '', esfera: '' });

    /* Estados do modal de exclusão */
    const [excluirAberto, setExcluirAberto]   = useState(false);
    const [imovelParaExcluir, setImovelParaExcluir] = useState(null);
    const [confirmacaoTexto, setConfirmacaoTexto]   = useState('');
    const [excluindo, setExcluindo]           = useState(false);
    const [erroExcluir, setErroExcluir]       = useState(null);

    /* ============================================================
       CARREGA
    ============================================================ */

    const carregar = useCallback(async () => {
        setLoading(true);
        setErro(null);

        try {
            const respLaudo = await fetch(
                `/api/laudos/buscar.php?codigo=${encodeURIComponent(codigo)}`,
                { credentials: 'include' }
            );
            const dataLaudo = await respLaudo.json();

            if (!dataLaudo.success) {
                throw new Error(dataLaudo.error || 'Laudo não encontrado');
            }

            setLaudo(dataLaudo.laudo);
            setForm({
                objetivo:   dataLaudo.laudo.objetivo   || '',
                finalidade: dataLaudo.laudo.finalidade || '',
                esfera:     dataLaudo.laudo.esfera     || '',
            });

            const laudoRef =
                dataLaudo.laudo.codigo_mh ||
                dataLaudo.laudo.codigo ||
                dataLaudo.laudo.uuid ||
                dataLaudo.laudo.id ||
                codigo;

            if (!laudoRef) {
                setImoveis([]);
                return;
            }

            const respImoveis = await fetch(
                `/api/imovel/carregar.php?codigo=${encodeURIComponent(laudoRef)}`,
                { credentials: 'include' }
            );

            if (!respImoveis.ok) {
                setImoveis([]);
                return;
            }

            const dataImoveis = await respImoveis.json();

            if (dataImoveis && dataImoveis.success) {
                const lista = dataImoveis.imoveis || dataImoveis.lista || [];
                setImoveis(Array.isArray(lista) ? lista : []);
            } else {
                setImoveis([]);
            }

        } catch (err) {
            console.error('[AbaAbertura]', err);
            setErro(err.message || 'Falha ao carregar');
        } finally {
            setLoading(false);
        }
    }, [codigo]);

    useEffect(() => { carregar(); }, [carregar]);

    /* ============================================================
       SALVAR EDIÇÃO DO LAUDO
    ============================================================ */

    const salvar = async () => {
        setErroModal(null);

        if (!form.objetivo || !form.finalidade || !form.esfera) {
            setErroModal('Objetivo, finalidade e esfera são obrigatórios.');
            return;
        }

        setSalvando(true);

        try {
            const resp = await fetch('/api/laudos/atualizar.php', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    codigo_mh: codigo,
                    objetivo: form.objetivo,
                    finalidade: form.finalidade,
                    esfera: form.esfera,
                }),
            });
            const data = await resp.json();

            if (!resp.ok || !data.success) {
                setErroModal(data.error || 'Falha ao salvar.');
                return;
            }

            setLaudo(prev => ({
                ...prev,
                objetivo:   data.objetivo,
                finalidade: data.finalidade,
                esfera:     data.esfera,
            }));
            setModalAberto(false);
        } catch (err) {
            setErroModal(err.message || 'Erro de comunicação.');
        } finally {
            setSalvando(false);
        }
    };

    /* ============================================================
       ABRIR MODAL DE EXCLUSÃO
    ============================================================ */

    const abrirExclusao = (imovel) => {
        setImovelParaExcluir(imovel);
        setConfirmacaoTexto('');
        setErroExcluir(null);
        setExcluirAberto(true);
    };

    const fecharExclusao = () => {
        setExcluirAberto(false);
        setImovelParaExcluir(null);
        setConfirmacaoTexto('');
        setErroExcluir(null);
    };

    /* ============================================================
       EXCLUIR
    ============================================================ */

    const confirmarExclusao = async () => {
        if (!imovelParaExcluir) return;

        const uuid = extrairUuidImovel(imovelParaExcluir);
        if (!uuid) {
            setErroExcluir('Imóvel sem UUID válido.');
            return;
        }

        // Confirmação: precisa digitar os 8 primeiros caracteres do UUID
        const esperado = uuid.slice(0, 8).toUpperCase();
        if (confirmacaoTexto.trim().toUpperCase() !== esperado) {
            setErroExcluir(`Digite exatamente os 8 primeiros caracteres do UUID: ${esperado}`);
            return;
        }

        setExcluindo(true);
        setErroExcluir(null);

        try {
            const resp = await fetch('/api/imovel/excluir.php', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ imovel_uuid: uuid }),
            });

            const data = await resp.json();

            if (!resp.ok || !data.success) {
                setErroExcluir(data.erro || data.error || 'Falha ao excluir.');
                return;
            }

            // Sucesso — recarrega a lista
            fecharExclusao();
            await carregar();
        } catch (err) {
            setErroExcluir(err.message || 'Erro de comunicação.');
        } finally {
            setExcluindo(false);
        }
    };

    /* ============================================================
       RENDER — estados de carregamento
    ============================================================ */

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="animate-spin text-amber-400" size={28} />
            </div>
        );
    }

    if (erro) {
        return (
            <div className="text-center py-20">
                <p className="text-red-400 font-bold text-sm uppercase tracking-widest">⚠ {erro}</p>
                <button
                    onClick={carregar}
                    className="mt-4 text-[10px] font-black uppercase text-slate-500 hover:text-white underline"
                >
                    Tentar novamente
                </button>
            </div>
        );
    }

    /* ============================================================
       RENDER PRINCIPAL
    ============================================================ */

    return (
        <div className="p-6 max-w-5xl mx-auto space-y-4">

            {/* HEADER */}
            <div className="bg-slate-900 rounded-2xl px-5 py-3.5 border border-white/5 shadow-lg flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 bg-amber-400 rounded-xl flex items-center justify-center shrink-0">
                        <FileText size={16} className="text-slate-950" />
                    </div>
                    <div className="min-w-0">
                        <h2 className="text-base font-black text-white uppercase italic tracking-tight leading-none">
                            Abertura do Laudo
                        </h2>
                        <p className="text-[8px] text-slate-500 uppercase tracking-[0.25em] font-black mt-1.5">
                            Dados iniciais e contexto
                        </p>
                    </div>
                </div>

                <button
                    onClick={() => setModalAberto(true)}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-400 hover:bg-white text-slate-950 text-[10px] font-black uppercase tracking-widest transition-all shadow-md"
                >
                    <Edit3 size={13} /> Editar
                </button>
            </div>

            {/* CONTEXTO DO LAUDO */}
            <div className="bg-slate-900 border border-white/5 rounded-2xl p-5">
                <h3 className="text-[10px] font-black text-amber-400 uppercase tracking-[0.25em] mb-4">
                    Contexto do Laudo
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <InfoItem icone={FileText} label="Código"     valor={laudo?.codigo_mh || '—'} />
                    <InfoItem icone={User}     label="Perito"     valor={laudo?.perito_nome || '—'} />
                    <InfoItem icone={Target}   label="Objetivo"   valor={laudo?.objetivo || '—'} />
                    <InfoItem icone={Gavel}    label="Finalidade" valor={laudo?.finalidade || '—'} />
                    <InfoItem icone={Gavel}    label="Esfera"     valor={laudo?.esfera || '—'} />
                    <InfoItem
                        icone={Clock}
                        label="Criado em"
                        valor={laudo?.data_criacao
                            ? new Date(laudo.data_criacao).toLocaleString('pt-BR')
                            : '—'}
                    />
                </div>
            </div>

            {/* IMÓVEIS DO LAUDO */}
            <div className="bg-slate-900 border border-white/5 rounded-2xl p-5">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-amber-400/10 border border-amber-400/20 flex items-center justify-center">
                            <MapPin size={16} className="text-amber-400" />
                        </div>
                        <div>
                            <h3 className="text-[10px] font-black text-amber-400 uppercase tracking-[0.25em]">
                                Imóveis do Laudo
                            </h3>
                            <p className="text-[8px] text-slate-500 uppercase tracking-widest font-black mt-1">
                                {imoveis.length} imóve{imoveis.length === 1 ? 'l' : 'is'}
                            </p>
                        </div>
                    </div>

                    <button
                        onClick={() => navigate(`/laudos/${codigo}/imovel/novo`)}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-400 hover:bg-white text-slate-950 text-[10px] font-black uppercase tracking-widest shadow-md transition-all"
                    >
                        <Plus size={13} /> Adicionar Imóvel
                    </button>
                </div>

                {imoveis.length === 0 ? (
                    <div className="text-center py-8">
                        <MapPin size={28} className="mx-auto text-slate-700 mb-3" />
                        <p className="text-slate-500 text-[11px] font-bold uppercase tracking-widest">
                            Nenhum imóvel cadastrado
                        </p>
                        <p className="text-slate-600 text-[9px] mt-1 uppercase tracking-widest">
                            Todo laudo precisa de ao menos 1 imóvel (NBR 14653)
                        </p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {imoveis.map((im, idx) => {
                            const uuid = extrairUuidImovel(im);
                            const endereco = enderecoDoImovel(im, idx);
                            const uuidCurto = (uuid || '').slice(0, 8) || '—';
                            const statusId = im?.status_imovel_id ?? im?.status ?? null;

                            // Badge de status
                            let badgeStatus = null;
                            if (statusId === 4) {
                                badgeStatus = (
                                    <span className="text-[7px] font-black uppercase tracking-widest text-rose-400 bg-rose-500/10 border border-rose-500/20 px-2 py-0.5 rounded-full">
                                        Não conforme
                                    </span>
                                );
                            } else if (statusId === 5) {
                                badgeStatus = (
                                    <span className="text-[7px] font-black uppercase tracking-widest text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full">
                                        Validado
                                    </span>
                                );
                            } else if (statusId === 3) {
                                badgeStatus = (
                                    <span className="text-[7px] font-black uppercase tracking-widest text-amber-400 bg-amber-400/10 border border-amber-400/20 px-2 py-0.5 rounded-full">
                                        Em validação
                                    </span>
                                );
                            }

                            return (
                                <div
                                    key={uuid || idx}
                                    className="flex items-center gap-2 p-3 rounded-xl bg-slate-950 border border-white/5 hover:border-amber-400/40 transition-all group"
                                >
                                    {/* Área clicável (navegar) */}
                                    <button
                                        onClick={() => {
                                            if (!uuid) return;
                                            navigate(`/laudos/${codigo}/imovel/novo?edit=${encodeURIComponent(uuid)}`);
                                        }}
                                        className="flex-1 min-w-0 flex items-center justify-between text-left"
                                    >
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <p className="text-white font-bold text-sm truncate">
                                                    {endereco}
                                                </p>
                                                {badgeStatus}
                                            </div>
                                            <p className="text-slate-500 text-[9px] uppercase tracking-wider mt-0.5 font-mono">
                                                UUID: {uuidCurto}...
                                            </p>
                                        </div>
                                        <ChevronRight
                                            size={14}
                                            className="text-slate-600 group-hover:text-amber-400 transition-colors shrink-0 ml-2"
                                        />
                                    </button>

                                    {/* Botão excluir */}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            abrirExclusao(im);
                                        }}
                                        title="Excluir imóvel"
                                        className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 hover:border-rose-500/30 transition-all flex items-center justify-center shrink-0"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* STATUS */}
            <div className="bg-slate-900 border border-white/5 rounded-2xl px-5 py-3 flex items-center justify-between">
                <span className="text-[9px] font-black text-slate-500 uppercase tracking-[0.25em]">
                    Status atual
                </span>
                <span className="px-3 py-1 rounded-full bg-amber-400/10 border border-amber-400/20 text-[9px] font-black text-amber-400 uppercase tracking-widest">
                    {laudo?.status?.replace(/_/g, ' ') || '—'}
                </span>
            </div>

            {/* ============================================================
                MODAL DE EDIÇÃO
            ============================================================ */}
            {modalAberto && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-lg p-6 shadow-2xl">

                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h3 className="text-base font-black text-white uppercase italic tracking-tight">
                                    Editar Laudo
                                </h3>
                                <p className="text-[9px] text-slate-500 uppercase tracking-widest font-mono mt-1">
                                    {codigo}
                                </p>
                            </div>
                            <button
                                onClick={() => setModalAberto(false)}
                                className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white transition-all"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <Select
                                label="Objetivo"
                                value={form.objetivo}
                                onChange={v => setForm({ ...form, objetivo: v })}
                                opcoes={OBJETIVOS}
                            />
                            <Select
                                label="Finalidade"
                                value={form.finalidade}
                                onChange={v => setForm({ ...form, finalidade: v })}
                                opcoes={FINALIDADES}
                            />
                            <Select
                                label="Esfera"
                                value={form.esfera}
                                onChange={v => setForm({ ...form, esfera: v })}
                                opcoes={ESFERAS}
                            />
                        </div>

                        {erroModal && (
                            <div className="mt-5 p-3 rounded-xl bg-red-500/10 border border-red-500/20 flex items-start gap-2">
                                <AlertTriangle size={14} className="text-red-400 shrink-0 mt-0.5" />
                                <p className="text-red-400 text-[10px] font-black uppercase tracking-widest leading-relaxed">
                                    {erroModal}
                                </p>
                            </div>
                        )}

                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={() => setModalAberto(false)}
                                disabled={salvando}
                                className="flex-1 px-5 py-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-white text-[10px] font-black uppercase tracking-widest transition-all disabled:opacity-50"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={salvar}
                                disabled={salvando}
                                className="flex-1 px-5 py-3 rounded-xl bg-amber-400 hover:bg-white disabled:opacity-50 text-slate-950 text-[10px] font-black uppercase tracking-widest shadow-md flex items-center justify-center gap-2 transition-all"
                            >
                                {salvando ? (
                                    <><Loader2 size={13} className="animate-spin" /> Salvando...</>
                                ) : (
                                    <><Save size={13} /> Salvar</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ============================================================
                MODAL DE EXCLUSÃO
            ============================================================ */}
            {excluirAberto && imovelParaExcluir && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
                    <div className="bg-slate-900 border border-rose-500/30 rounded-2xl w-full max-w-lg p-6 shadow-2xl">

                        {/* HEADER */}
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-rose-500/10 border border-rose-500/30 flex items-center justify-center">
                                    <ShieldAlert size={18} className="text-rose-400" />
                                </div>
                                <div>
                                    <h3 className="text-base font-black text-white uppercase italic tracking-tight">
                                        Excluir Imóvel
                                    </h3>
                                    <p className="text-[9px] text-rose-400 uppercase tracking-widest font-black mt-1">
                                        Ação irreversível
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={fecharExclusao}
                                className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white transition-all"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* AVISO */}
                        <div className="bg-rose-500/5 border border-rose-500/20 rounded-xl p-4 mb-5">
                            <p className="text-[10px] text-rose-200 leading-relaxed">
                                Você está prestes a excluir o imóvel:
                            </p>
                            <p className="text-white font-bold text-sm mt-2">
                                {enderecoDoImovel(imovelParaExcluir, 0)}
                            </p>
                            <p className="text-slate-500 text-[9px] uppercase tracking-wider mt-1 font-mono">
                                UUID: {extrairUuidImovel(imovelParaExcluir)}
                            </p>
                            <p className="text-[10px] text-rose-300 mt-3 leading-relaxed">
                                <strong>Todos os dados vinculados serão apagados:</strong>
                            </p>
                            <ul className="text-[10px] text-rose-200 mt-1 space-y-0.5 ml-4 list-disc">
                                <li>Tarefas automáticas</li>
                                <li>Histórico de validação</li>
                                <li>Divergências e inconsistências</li>
                            </ul>
                            <p className="text-[10px] text-rose-300 mt-3">
                                Esta ação <strong>não pode ser desfeita</strong>.
                            </p>
                        </div>

                        {/* CONFIRMAÇÃO */}
                        <label className="block mb-5">
                            <span className="text-[9px] font-black uppercase text-amber-400 tracking-widest block mb-2">
                                Para confirmar, digite os 8 primeiros caracteres do UUID:
                            </span>
                            <code className="block text-amber-400 font-mono text-sm mb-2">
                                {extrairUuidImovel(imovelParaExcluir)?.slice(0, 8).toUpperCase()}
                            </code>
                            <input
                                type="text"
                                value={confirmacaoTexto}
                                onChange={(e) => setConfirmacaoTexto(e.target.value)}
                                placeholder="Digite aqui"
                                maxLength={8}
                                autoFocus
                                className="w-full bg-slate-950 border border-white/10 rounded-xl px-4 py-3 text-white font-mono text-center uppercase tracking-[0.5em] outline-none focus:border-rose-400 transition-all"
                            />
                        </label>

                        {erroExcluir && (
                            <div className="mb-5 p-3 rounded-xl bg-red-500/10 border border-red-500/20 flex items-start gap-2">
                                <AlertTriangle size={14} className="text-red-400 shrink-0 mt-0.5" />
                                <p className="text-red-400 text-[10px] font-black uppercase tracking-widest leading-relaxed">
                                    {erroExcluir}
                                </p>
                            </div>
                        )}

                        {/* AÇÕES */}
                        <div className="flex gap-3">
                            <button
                                onClick={fecharExclusao}
                                disabled={excluindo}
                                className="flex-1 px-5 py-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-white text-[10px] font-black uppercase tracking-widest transition-all disabled:opacity-50"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={confirmarExclusao}
                                disabled={
                                    excluindo ||
                                    confirmacaoTexto.trim().toUpperCase() !==
                                        extrairUuidImovel(imovelParaExcluir)?.slice(0, 8).toUpperCase()
                                }
                                className="flex-1 px-5 py-3 rounded-xl bg-rose-500 hover:bg-rose-600 disabled:bg-slate-800 disabled:text-slate-600 disabled:cursor-not-allowed text-white text-[10px] font-black uppercase tracking-widest shadow-md flex items-center justify-center gap-2 transition-all"
                            >
                                {excluindo ? (
                                    <><Loader2 size={13} className="animate-spin" /> Excluindo...</>
                                ) : (
                                    <><Trash2 size={13} /> Excluir definitivamente</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ============================================================
   SUB-COMPONENTES
============================================================ */

function InfoItem({ icone: Icone, label, valor }) {
    return (
        <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                <Icone size={13} className="text-amber-400" />
            </div>
            <div className="min-w-0">
                <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest mb-1">
                    {label}
                </p>
                <p className="text-white font-bold text-[12px] truncate">
                    {valor}
                </p>
            </div>
        </div>
    );
}

function Select({ label, value, onChange, opcoes }) {
    return (
        <div>
            <label className="text-[9px] font-black text-amber-400 uppercase tracking-[0.25em] mb-2 block">
                {label}
            </label>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-slate-950 border border-white/10 text-white font-bold text-sm uppercase tracking-wider focus:border-amber-400 focus:outline-none transition-all cursor-pointer"
            >
                <option value="" className="bg-slate-900">Selecione {label.toLowerCase()}</option>
                {opcoes.map(o => (
                    <option key={o} value={o} className="bg-slate-900">{o}</option>
                ))}
            </select>
        </div>
    );
}